import { analysisDisplayText } from '../utils/analysisDisplayText';
import React, { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from './ui/button';
import { Phase5ReportTitle } from './Phase5ReportSystem';
import Phase5ReportView from './Phase5ReportView';
import RichReportRenderer from './RichReportRenderer';

const timestampOf = (report) => report?.generated_at || report?.created_at || '';
const recordKey = (report, originalIndex) => report?.id || report?._id || `legacy-${originalIndex}`;

export const newestFirstCustomReports = (reports = []) => reports
  .map((report, originalIndex) => ({ report, originalIndex, key: recordKey(report, originalIndex) }))
  .filter(({ report }) => report?.is_custom === true)
  .sort((a, b) => {
    const timeDifference = Date.parse(timestampOf(b.report)) - Date.parse(timestampOf(a.report));
    return Number.isNaN(timeDifference) || timeDifference === 0 ? b.originalIndex - a.originalIndex : timeDifference;
  });

const generatedByFor = (report, currentUser) => report?.psychologist_name
  || (report?.psychologist_id === currentUser?.id ? currentUser?.name : null)
  || report?.generated_by
  || report?.psychologist_id
  || 'Unknown';

export default function CustomReportHistory({ reports, sessionId, currentUser, onDownload, onDelete }) {
  const [pageSize, setPageSize] = useState(5);
  const [page, setPage] = useState(1);
  const [selectedKey, setSelectedKey] = useState(null);
  const rows = useMemo(() => newestFirstCustomReports(reports), [reports]);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const visibleRows = rows.slice((page - 1) * pageSize, page * pageSize);
  const selectedRow = rows.find(({ key }) => key === selectedKey) || null;

  useEffect(() => { setPage((current) => Math.min(current, pageCount)); }, [pageCount]);
  useEffect(() => {
    if (selectedKey && !rows.some(({ key }) => key === selectedKey)) setSelectedKey(null);
  }, [rows, selectedKey]);

  return <section className="mt-6" data-testid="custom-report-generation-history">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h5 className="text-lg font-semibold">Analysis History</h5>
      <label className="flex items-center gap-2 text-sm" htmlFor="custom-history-page-size">
        Show
        <select id="custom-history-page-size" aria-label="Analysis per page" value={pageSize}
          onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}
          className="rounded border border-gray-300 bg-white px-2 py-1">
          <option value={5}>5</option><option value={10}>10</option><option value={100}>100</option>
        </select>
      </label>
    </div>
    <div className="mt-3 overflow-x-auto rounded-lg border bg-white">
      <table className="w-full text-left text-sm" data-testid="custom-report-history-table">
        <thead className="bg-gray-50 text-gray-700"><tr>
          <th className="px-3 py-2 font-semibold">Analysis Name</th>
          <th className="px-3 py-2 font-semibold">Analysis Done By</th>
          <th className="px-3 py-2 font-semibold">Date / Time</th>
          <th className="px-3 py-2 font-semibold">Actions</th>
        </tr></thead>
        <tbody>
          {visibleRows.map(({ report, originalIndex, key }) => <tr key={key} className="border-t" data-testid={`custom-history-row-${key}`}>
            <td className="px-3 py-2 font-medium">{analysisDisplayText(report.report_name || 'Custom Analysis')}</td>
            <td className="px-3 py-2">{generatedByFor(report, currentUser)}</td>
            <td className="px-3 py-2">{timestampOf(report) ? new Date(timestampOf(report)).toLocaleString() : 'Unknown'}</td>
            <td className="px-3 py-2"><div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setSelectedKey(key)} data-testid={`custom-open-${key}`}>Open</Button>
              <Button size="sm" variant="outline" onClick={() => onDownload(report)} data-testid={`custom-download-${key}`}><Download className="mr-1 h-4 w-4" />Download PDF</Button>
              <Button size="sm" variant="destructive" onClick={() => onDelete(report, originalIndex)} data-testid={`custom-delete-${key}`}>Delete</Button>
            </div></td>
          </tr>)}
          {!visibleRows.length && <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-500">No Custom Analysis generations yet.</td></tr>}
        </tbody>
      </table>
    </div>
    {pageCount > 1 && <nav className="mt-3 flex items-center justify-center gap-3" aria-label="Custom Analysis history pages">
      <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((value) => value - 1)}>Previous</Button>
      <span className="text-sm" data-testid="custom-history-page-indicator">Page {page} of {pageCount}</span>
      <Button size="sm" variant="outline" disabled={page === pageCount} onClick={() => setPage((value) => value + 1)}>Next</Button>
    </nav>}
    {selectedRow && <div className="mt-6 rounded-lg border bg-white p-4" data-testid="selected-custom-report">
      <Phase5ReportTitle title={selectedRow.report.report_name || 'Custom Analysis'}
        actions={<Button type="button" variant="outline" onClick={() => setSelectedKey(null)} data-testid="custom-close">Close</Button>}
        generatedByLabel="Analysis Done By" generatedAtLabel="Date / Time"
        generatedBy={generatedByFor(selectedRow.report, currentUser)} generatedAt={timestampOf(selectedRow.report)} />
      <Phase5ReportView sessionId={sessionId} reportName={selectedRow.report.report_name || 'Custom Analysis'} reportKey={`custom_report_history_${selectedRow.key}`}>
        {({ expandedSectionIds, onExpandedSectionIdsChange, onSectionIdsChange }) => <RichReportRenderer
          content={selectedRow.report.content || selectedRow.report.report_content || ''}
          testId={`custom-report-rich-${selectedRow.key}`} sessionId={sessionId} reportKey={`custom_report_history_${selectedRow.key}`}
          expandedSectionIds={expandedSectionIds} onExpandedSectionIdsChange={onExpandedSectionIdsChange} onSectionIdsChange={onSectionIdsChange} />}
      </Phase5ReportView>
    </div>}
  </section>;
}
